// v10/src/components/SectionTip.js
// Aparece 1 sola vez por sección. Se descarta automáticamente o con tap.

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Tips por nombre de sección (parcial, case-insensitive)
const SECTION_TIPS = {
  fluidos:      { emoji: '🛢', title: 'Sección Fluidos', tip: 'Verificá combustible y aceite visualmente. Si hay dudas, medí con la varilla.' },
  combustible:  { emoji: '⛽', title: 'Combustible',     tip: 'Drenaje primero: buscá agua o sedimentos en el sumple. Luego nivel en cada tanque.' },
  aceite:       { emoji: '🛢', title: 'Aceite',           tip: 'Verificá entre el mínimo y máximo del fabricante. Nunca volar por debajo del mínimo.' },
  exterior:     { emoji: '🔍', title: 'Inspección Exterior', tip: 'Caminá alrededor del avión en sentido horario. Tocá superficies de control.' },
  motor:        { emoji: '⚙️', title: 'Motor',            tip: 'Buscá pérdidas, cables sueltos o daños en la hélice. Verificá tapas y accesos.' },
  cabina:       { emoji: '🎛', title: 'Cabina',           tip: 'Configurá altímetro, radio y transpondedor antes de iniciar el motor.' },
  documentaci:  { emoji: '📋', title: 'Documentación',   tip: 'Recordá CARPETA: Certificado, Autorización, Radio, Peso, Emergencias, Técnico, Asegurado.' },
  tren:         { emoji: '🛞', title: 'Tren de Aterrizaje', tip: 'Verificá presión de neumáticos y frenos. Buscá grietas o desgaste excesivo.' },
  helice:       { emoji: '🌀', title: 'Hélice',           tip: 'Inspeccioná erosión, marcas o grietas. El balance es crítico para el motor.' },
  comunic:      { emoji: '📻', title: 'Comunicaciones',   tip: 'Probá radio en tierra antes de encender el motor. Verificá frecuencias.' },
};

const getTipForSection = (sectionName) => {
  const key = sectionName?.toLowerCase() || '';
  for (const [k, tip] of Object.entries(SECTION_TIPS)) {
    if (key.includes(k)) return tip;
  }
  return null;
};

const STORAGE_KEY = 'condor360_seen_tips';

const getSeenTips = () => {
  try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
};

const markTipSeen = (sectionName) => {
  try {
    const seen = getSeenTips();
    if (!seen.includes(sectionName)) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...seen, sectionName]));
    }
  } catch {}
};

export const clearSeenTips = () => {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
};

// ── Componente ────────────────────────────────────────────────────
const SectionTip = ({ sectionName, darkMode, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const tip = getTipForSection(sectionName);

  useEffect(() => {
    if (!tip || !sectionName) return;
    const seen = getSeenTips();
    if (seen.includes(sectionName)) return;

    // Pequeño delay para que no aparezca al mismo tiempo que la sección
    const t1 = setTimeout(() => setVisible(true), 600);
    // Auto-dismiss después de 5 segundos
    const t2 = setTimeout(() => dismiss(), 5600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [sectionName]);

  const dismiss = () => {
    setVisible(false);
    markTipSeen(sectionName);
    onDismiss?.();
  };

  if (!tip) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.97 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          onClick={dismiss}
          style={{
            position: 'fixed', bottom: '80px', left: '16px', right: '16px',
            zIndex: 60, cursor: 'pointer',
            // En desktop: centrado y más angosto
            maxWidth: '420px', margin: '0 auto',
          }}
        >
          <div style={{
            borderRadius: '20px', overflow: 'hidden',
            background: darkMode ? 'rgba(12,18,32,0.97)' : 'rgba(255,255,255,0.97)',
            backdropFilter: 'blur(20px)',
            border: darkMode ? '1px solid rgba(59,130,246,0.2)' : '1px solid rgba(59,130,246,0.15)',
            boxShadow: darkMode
              ? '0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(59,130,246,0.1)'
              : '0 8px 32px rgba(59,130,246,0.12)',
          }}>
            {/* Progress bar que se agota en 5s */}
            <div style={{ height: '3px', background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)', position: 'relative', overflow: 'hidden' }}>
              <motion.div
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: 5, ease: 'linear' }}
                style={{ height: '100%', background: 'linear-gradient(90deg, #3b82f6, #6d28d9)', position: 'absolute', right: 0 }}
              />
            </div>

            <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              {/* Icon */}
              <div style={{
                width: '38px', height: '38px', borderRadius: '12px', flexShrink: 0,
                background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(109,40,217,0.15))',
                border: '1px solid rgba(59,130,246,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '20px',
              }}>
                {tip.emoji}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{
                    fontSize: '10px', fontWeight: 800, textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    color: darkMode ? 'rgba(147,197,253,0.8)' : '#2563eb',
                  }}>
                    💡 Tip — {tip.title}
                  </span>
                  <span style={{ fontSize: '11px', color: darkMode ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.25)', fontWeight: 500 }}>
                    Tocá para cerrar
                  </span>
                </div>
                <p style={{
                  margin: 0, fontSize: '13.5px', fontWeight: 500, lineHeight: 1.5,
                  color: darkMode ? 'rgba(255,255,255,0.8)' : 'rgba(15,25,55,0.8)',
                }}>
                  {tip.tip}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SectionTip;
